import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { CnetAuthenticationRequest } from '../checkout-model';
import { EnvService } from '../../env.service';
import { SessionVariable } from '../../common/enum/session-variable';
import { LocalStorageService } from '../../common/services/local-storage.service';
import { SharedSignalsService } from '../../common/services/shared-signals.service';

export interface CnetAuthContext {
  civilId: string;
  requestId: string;
  referenceNo?: string;
}

export type CnetAuthTarget = 'newUser' | 'existing-user';

@Injectable({
  providedIn: 'root',
})
export class CnetAuthSessionService {
  private readonly router = inject(Router);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly sharedSignalsService = inject(SharedSignalsService);
  private readonly envService = inject(EnvService);
  private readonly sanitizer = inject(DomSanitizer);

  resolveCivilId(): string | null {
    return this.persistCivilId(this.readSources().civilId.value);
  }

  persistCivilId(civilId: unknown): string | null {
    const value = this.normalize(civilId);

    if (value == null) {
      return null;
    }

    this.sharedSignalsService.setNationalId(value);
    this.localStorageService.setItem(SessionVariable.NATIONAL_ID, value);

    return value;
  }

  persistRequestId(requestId: unknown): string | null {
    const value = this.normalize(requestId);

    if (value == null) {
      return null;
    }

    this.sharedSignalsService.setRequestId(value);
    this.localStorageService.setItem(SessionVariable.REQ_ID, value);

    return value;
  }

  resolve(): CnetAuthContext | null {
    const sources = this.readSources();
    const civilId = this.normalize(sources.civilId.value);
    const requestId = this.normalize(sources.requestId.value);
    const referenceNo = this.normalize(sources.referenceNo.value) || requestId;

    if (civilId == null || requestId == null) {
      console.warn('[CINET auth] incomplete session', {
        url: this.router.url,
        civilIdPresent: civilId != null,
        requestIdPresent: requestId != null,
        civilIdSource: sources.civilId.source,
        requestIdSource: sources.requestId.source,
      });

      return null;
    }

    this.persistCivilId(civilId);
    this.persistRequestId(requestId);

    return { civilId, requestId, referenceNo: referenceNo ?? undefined };
  }

  toRequest(): CnetAuthenticationRequest | null {
    return this.resolve();
  }

  navigateToAuthentication(route: ActivatedRoute, target: CnetAuthTarget): boolean {
    const context = this.resolve();

    if (!context) {
      this.router.navigate(['../mobile'], { relativeTo: route });

      return false;
    }

    this.router.navigate([`../cnet-authentication-${target}`], {
      relativeTo: route,
      state: context,
    });

    return true;
  }

  returnToMobile(route: ActivatedRoute): void {
    this.router.navigate(['../mobile'], { relativeTo: route });
  }

  private readSources(): {
    civilId: { value: unknown; source: string };
    requestId: { value: unknown; source: string };
    referenceNo: { value: unknown; source: string };
  } {
    const state = this.readNavigationState();

    return {
      civilId: this.firstPresent([
        { value: state?.civilId, source: 'navigation' },
        { value: this.sharedSignalsService.nationalId(), source: 'signal' },
        {
          value: this.localStorageService.getItem(SessionVariable.NATIONAL_ID),
          source: 'storage',
        },
        { value: this.readAuthProfileNationalId(), source: 'profile' },
      ]),
      requestId: this.firstPresent([
        { value: state?.requestId, source: 'navigation' },
        { value: state?.referenceNo, source: 'navigation' },
        { value: this.sharedSignalsService.requestId(), source: 'signal' },
        { value: this.localStorageService.getItem(SessionVariable.REQ_ID), source: 'storage' },
      ]),
      referenceNo: this.firstPresent([
        { value: state?.referenceNo, source: 'navigation' },
        { value: state?.requestId, source: 'navigation' },
        { value: this.sharedSignalsService.requestId(), source: 'signal' },
        { value: this.localStorageService.getItem(SessionVariable.REQ_ID), source: 'storage' },
      ]),
    };
  }

  private readAuthProfileNationalId(): unknown {
    const raw = this.localStorageService.getItem<unknown>(SessionVariable.AUTH_PROFILE);

    if (raw == null) {
      return null;
    }

    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw)?.nationalId;
      } catch {
        return null;
      }
    }

    if (typeof raw === 'object') {
      return (raw as { nationalId?: unknown })?.nationalId;
    }

    return null;
  }

  private readNavigationState(): Partial<CnetAuthContext> | null {
    const state = this.router.getCurrentNavigation()?.extras?.state ?? window.history.state;

    if (!state) {
      return null;
    }

    return {
      civilId: state.civilId,
      requestId: state.requestId,
      referenceNo: state.referenceNo,
    };
  }

  isMobileUserAgent(): boolean {
    return this.isAndroidUserAgent() || this.isIOSUserAgent();
  }

  isAndroidUserAgent(): boolean {
    const osInfo = this.sharedSignalsService?.userAgentType();
    if (osInfo === 'Android') return true;
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      return /Android/i.test(navigator.userAgent);
    }
    return false;
  }

  isIOSUserAgent(): boolean {
    const osInfo = this.sharedSignalsService?.userAgentType();
    if (osInfo === 'iOS') return true;
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    }
    return false;
  }

  normalizeDeepLink(linkOrRid?: string | null): string {
    const rawInput = (linkOrRid ?? '').trim();
    if (/^pacimid:\/\//i.test(rawInput)) {
      return rawInput.replace(/^pacimid:\/\//i, 'pacimid://');
    }
    return rawInput;
  }

  getAndroidIntentUrl(targetDeepLink: string, playStoreUrl: string): string {
    if (targetDeepLink.startsWith('intent://')) {
      return targetDeepLink;
    }
    const rid = this.extractRid(targetDeepLink) || '';
    const queryPart = targetDeepLink.includes('?')
      ? targetDeepLink.split('?')[1]
      : rid
        ? `RID=${rid}`
        : '';

    return `intent://Send?${queryPart}#Intent;scheme=pacimid;package=kw.gov.paci.PACIMobileID;S.browser_fallback_url=${encodeURIComponent(playStoreUrl)};end`;
  }

  getKmidDeepLink(linkOrRid?: string | null): string {
    const rawInput = (linkOrRid ?? '').trim();
    const normalized = this.normalizeDeepLink(rawInput);
    const playStoreUrl = this.envService.cneAappAndroidURL;
    const appStoreUrl = this.envService.cneAappIOSURL;

    let paciUrl = '';
    if (normalized.startsWith('pacimid://')) {
      paciUrl = normalized;
    } else {
      const rid =
        this.extractRid(rawInput) ||
        this.normalize(this.sharedSignalsService.requestId()) ||
        this.normalize(this.localStorageService.getItem(SessionVariable.REQ_ID));

      if (rid) {
        paciUrl = `pacimid://Send?RID=${rid}`;
      }
    }

    if (!paciUrl) {
      return this.isIOSUserAgent() ? appStoreUrl : playStoreUrl;
    }

    if (this.isAndroidUserAgent()) {
      return this.getAndroidIntentUrl(paciUrl, playStoreUrl);
    }

    return paciUrl;
  }

  getKmidSafeDeepLink(linkOrRid?: string | null): SafeUrl | string {
    const rawUrl = this.getKmidDeepLink(linkOrRid);
    if (rawUrl.startsWith('pacimid://') || rawUrl.startsWith('intent://')) {
      return this.sanitizer.bypassSecurityTrustUrl(rawUrl);
    }
    return rawUrl;
  }

  handleIosFallback(): void {}

  openKmidApp(linkOrRid?: string | null): void {
    const isMobile = this.isMobileUserAgent();
    const playStoreUrl = this.envService.cneAappAndroidURL;
    const appStoreUrl = this.envService.cneAappIOSURL;

    const targetDeepLink = this.getKmidDeepLink(linkOrRid);

    if (!isMobile) {
      const fallbackUrl = this.isIOSUserAgent() ? appStoreUrl : playStoreUrl;
      window.open(fallbackUrl, '_blank');
      return;
    }

    if (this.isIOSUserAgent()) {
      // Native iOS deep linking handled by href
    }

    if (targetDeepLink.startsWith('pacimid://') || targetDeepLink.startsWith('intent://')) {
      const a = document.createElement('a');
      a.href = targetDeepLink;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      window.location.href = targetDeepLink;
    }
  }

  extractRid(input?: string | null): string | null {
    if (!input) return null;
    const trimmed = String(input).trim();
    if (!trimmed) return null;

    const match = trimmed.match(/RID=([^&"#\s]+)/i);
    if (match && match[1]) {
      return match[1];
    }

    if (!trimmed.includes(':') && !trimmed.includes('?')) {
      return trimmed;
    }

    return null;
  }

  private firstPresent(candidates: { value: unknown; source: string }[]): {
    value: unknown;
    source: string;
  } {
    for (const candidate of candidates) {
      if (this.normalize(candidate.value) != null) {
        return candidate;
      }
    }

    return { value: null, source: 'missing' };
  }

  private normalize(value: unknown): string | null {
    if (value == null || value === '') {
      return null;
    }

    const normalized = String(value).trim();

    if (!normalized || normalized === 'null' || normalized === 'undefined') {
      return null;
    }

    return normalized;
  }
}
